import React, { useCallback, useEffect, useSyncExternalStore } from 'react';

// Tipos para el estado de red
export type NetworkStatus = 'online' | 'offline' | 'slow';

export interface NetworkState {
  isOnline: boolean;
  isOffline: boolean;
  isSlow: boolean;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
}

// Extend Navigator type for Network Information API
interface NetworkInformation {
  effectiveType: '4g' | '3g' | '2g' | 'slow-2g';
  downlink: number;
  rtt: number;
  saveData: boolean;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

// Obtiene la conexión de red del navegador
function getConnection(): NetworkInformation | undefined {
  const nav = navigator as NavigatorWithConnection;
  return nav.connection || nav.mozConnection || nav.webkitConnection;
}

// Store para manejar subscripciones
let listeners: Array<() => void> = [];

// Cache del último snapshot para evitar crear nuevos objetos innecesariamente
let cachedSnapshot: NetworkState | null = null;

function subscribe(listener: () => void): () => void {
  listeners = [...listeners, listener];

  // Eventos de online/offline
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);

  // Evento de cambio de conexión (si está disponible)
  const connection = getConnection();
  if (connection) {
    connection.addEventListener('change', listener);
  }

  return () => {
    listeners = listeners.filter((l) => l !== listener);
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);

    if (connection) {
      connection.removeEventListener('change', listener);
    }
  };
}

function getSnapshot(): NetworkState {
  const connection = getConnection();
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  // Determina si la conexión es lenta basándose en el tipo de conexión efectiva
  const effectiveType = connection?.effectiveType || null;
  const isSlow = effectiveType === '2g' || effectiveType === 'slow-2g';
  const downlink = connection?.downlink || null;
  const rtt = connection?.rtt || null;

  // Solo crear nuevo objeto si los valores cambiaron
  if (
    cachedSnapshot &&
    cachedSnapshot.isOnline === isOnline &&
    cachedSnapshot.isSlow === isSlow &&
    cachedSnapshot.effectiveType === effectiveType &&
    cachedSnapshot.downlink === downlink &&
    cachedSnapshot.rtt === rtt
  ) {
    return cachedSnapshot;
  }

  cachedSnapshot = {
    isOnline,
    isOffline: !isOnline,
    isSlow,
    effectiveType,
    downlink,
    rtt,
  };

  return cachedSnapshot;
}

const serverSnapshot: NetworkState = {
  isOnline: true,
  isOffline: false,
  isSlow: false,
  effectiveType: null,
  downlink: null,
  rtt: null,
};

function getServerSnapshot(): NetworkState {
  // En SSR, asumimos que hay conexión - retornamos siempre el mismo objeto
  return serverSnapshot;
}

/**
 * Hook para detectar el estado de conexión a internet
 *
 * @example
 * const { isOnline, isOffline, isSlow } = useNetworkStatus();
 *
 * if (isOffline) {
 *   return <OfflineBanner />;
 * }
 */
export function useNetworkStatus(): NetworkState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Hook para verificar conectividad real haciendo un ping
 * Útil cuando navigator.onLine puede dar falsos positivos
 */
export function useNetworkPing(
  pingUrl = '/health.txt',
  interval = 30000
): {
  isReachable: boolean | null;
  lastCheck: Date | null;
  checkNow: () => Promise<boolean>;
} {
  const { isOnline } = useNetworkStatus();
  const [state, setState] = React.useState<{
    isReachable: boolean | null;
    lastCheck: Date | null;
  }>({
    isReachable: null,
    lastCheck: null,
  });

  const checkConnectivity = useCallback(async (): Promise<boolean> => {
    if (!isOnline) {
      setState({ isReachable: false, lastCheck: new Date() });
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Para minimizar costos y evitar invocar funciones, el ping por defecto apunta
      // a un recurso estático (CDN). Usamos GET para máxima compatibilidad.
      const cacheBustedUrl = `${pingUrl}${pingUrl.includes('?') ? '&' : '?'}_ts=${Date.now()}`;
      const response = await fetch(cacheBustedUrl, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const isReachable = response.ok;
      setState({ isReachable, lastCheck: new Date() });
      return isReachable;
    } catch {
      setState({ isReachable: false, lastCheck: new Date() });
      return false;
    }
  }, [isOnline, pingUrl]);

  useEffect(() => {
    // Verificar al montar
    checkConnectivity();

    // Verificar periódicamente
    const intervalId = setInterval(checkConnectivity, interval);

    return () => clearInterval(intervalId);
  }, [checkConnectivity, interval]);

  // Re-verificar cuando cambie el estado online
  useEffect(() => {
    if (isOnline) {
      checkConnectivity();
    }
  }, [isOnline, checkConnectivity]);

  return {
    ...state,
    checkNow: checkConnectivity,
  };
}

export default useNetworkStatus;
